import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import type { AppNotification } from '../types/notifications';
import { NotificationRow } from './notifications/NotificationRow';
import { notificationsListPath } from '../utils/notificationRoutes';

type Props = {
  open: boolean;
  panelFixed: { top: number; right: number } | null;
  notifications: AppNotification[];
  unreadCount: number;
  onClose: () => void;
  onMarkRead: (id: string) => void;
  onArchive: (n: AppNotification) => void;
  panelRef: React.RefObject<HTMLDivElement | null>;
};

export function NotificationsPanel({
  open,
  panelFixed,
  notifications,
  unreadCount,
  onClose,
  onMarkRead,
  onArchive,
  panelRef,
}: Props) {
  const navigate = useNavigate();

  const handleOpen = (n: AppNotification) => {
    if (!n.is_read) void onMarkRead(n.id);
    onClose();
    if (n.link) navigate(n.link);
  };

  if (!open || !panelFixed) return null;

  return createPortal(
    <div
      ref={panelRef}
      className="fixed w-[min(380px,calc(100vw-16px))] rounded-lg border border-app-border bg-app-surface shadow-2xl z-[10000] flex flex-col max-h-[min(520px,calc(100vh-80px))]"
      style={{ top: panelFixed.top, right: panelFixed.right }}
      role="dialog"
      aria-label="Benachrichtigungen"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-app-border">
        <h2 className="text-base font-semibold text-app-text">Benachrichtigungen</h2>
        <button
          type="button"
          className="p-1.5 rounded-md text-slate-400 hover:text-app-text-muted hover:bg-app-hover"
          title="Schließen"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <p className="px-4 py-6 text-sm text-app-text-subtle">Keine Benachrichtigungen</p>
        ) : (
          <ul>
            {notifications.map((n) => (
              <NotificationRow
                key={n.id}
                notification={n}
                onOpen={handleOpen}
                action="archive"
                onAction={onArchive}
              />
            ))}
          </ul>
        )}
      </div>

      <div className="border-t border-app-border py-2.5 text-center">
        <button
          type="button"
          className="text-xs font-semibold tracking-wide text-pipedrive-blue hover:underline uppercase"
          onClick={() => {
            onClose();
            navigate(notificationsListPath());
          }}
        >
          Alle anzeigen{unreadCount > 0 ? ` (${unreadCount})` : ''}
        </button>
      </div>
    </div>,
    document.body,
  );
}
